import { __rest, __assign } from '../../external/tslib/tslib.es6.mjs.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useRef, useEffect, useImperativeHandle } from 'react';
import clsx from 'clsx';
import { FormElement } from '../formElement/FormElement.js';
import '../../base/addButton/AddButton.module.scss.js';
import '../../base/alert/Alert.js';
import '../../base/breadcrumb/Breadcrumb.module.scss.js';
import '../../base/button/Button.js';
import 'react-aria';
import 'react-stately';
import { IconButton } from '../../base/iconButton/IconButton.js';
import '../../base/calendar/Calendar.module.scss.js';
import 'dayjs';
import '../../base/calendar/calendarGrid/CalendarGrid.module.scss.js';
import '../../base/calendar/calendarGrid/calendarCell/CalendarCell.module.scss.js';
import '../../base/carBackground/CarBackground.module.scss.js';
import '../../base/loader/Loader.module.scss.js';
import '../../base/carLoader/CarLoader.module.scss.js';
import '../../base/card/Card.js';
import '../../base/confirmation/Confirmation.js';
import '../../base/modal/Modal.js';
import '../../base/confirmationModal/ConfirmationModal.module.scss.js';
import '../../base/error/Error.module.scss.js';
import { CloseIcon } from '../../icons/CloseIcon.js';
import '../../base/tag/Tag.module.scss.js';
import '../../base/vehicleQuote/VehicleQuote.module.scss.js';
import '../../base/insuranceQuote/InsuranceQuote.module.scss.js';
import '../../base/listBox/ListBox.js';
import '../../base/listCard/ListCard.js';
import 'react-dom';
import 'react-transition-group';
import '../../base/popover/Popover.module.scss.js';
import '../../base/progressBar/ProgressBar.module.scss.js';
import '../../base/toast/context/ToastContext.js';
import '../../base/toast/Toast.module.scss.js';
import '../../base/toast/context/ToastContext.module.scss.js';
import '../../base/tooltip/Tooltip.module.scss.js';
import { useSetNativeValue } from '../../hooks/useSetNativeValue/useSetNativeValue.js';
import styles from './Input.module.scss.js';
import { Skeleton } from './skeleton/Skeleton.js';
import { Icon } from './icon/Icon.js';
import { TriggerButton } from './triggerButton/TriggerButton.js';
import { InputContextProvider } from './context/InputContext.js';

var getRightLeftElType = function (element) {
    if (!element || typeof element === 'string' || typeof element === 'number' || typeof element === 'boolean') {
        return 'unknown';
    }
    if ((element === null || element === void 0 ? void 0 : element.type) === TriggerButton) {
        return 'trigger';
    }
    if ((element === null || element === void 0 ? void 0 : element.type) === Icon) {
        return 'icon';
    }
    return 'unknown';
};
var InputElement = forwardRef(function (_a, ref) {
    var _b;
    var _c;
    var name = _a.name, label = _a.label, className = _a.className; _a.style; var _d = _a.type, type = _d === void 0 ? 'text' : _d, _e = _a.size, size = _e === void 0 ? 'm' : _e, id = _a.id, _f = _a["aria-describedby"], ariaDescribedby = _f === void 0 ? '' : _f, isDisabled = _a.disabled, hintMessage = _a.hintMessage, hasError = _a.hasError, hasWarning = _a.hasWarning, autoFocus = _a.autoFocus, required = _a.required, labelId = _a.labelId, showMaxLengthMessage = _a.showMaxLengthMessage, inputClassName = _a.inputClassName, inputStyle = _a.inputStyle, inputProps = _a.inputProps, hideAsterisk = _a.hideAsterisk, rightElement = _a.rightElement, leftElement = _a.leftElement, wrapperClassName = _a.wrapperClassName, isReadOnly = _a.readOnly, isClearable = _a.isClearable, showOptionalLabel = _a.showOptionalLabel, props = __rest(_a, ["name", "label", "className", "style", "type", "size", "id", 'aria-describedby', "disabled", "hintMessage", "hasError", "hasWarning", "autoFocus", "required", "labelId", "showMaxLengthMessage", "inputClassName", "inputStyle", "inputProps", "hideAsterisk", "rightElement", "leftElement", "wrapperClassName", "readOnly", "isClearable", "showOptionalLabel"]);
    var setNativeValue = useSetNativeValue();
    var inputRef = useRef(null);
    var hintMessageId = hintMessage ? "".concat(name, "--desc") : '';
    var leftElementType = getRightLeftElType(leftElement);
    var rightElementType = getRightLeftElType(rightElement);
    useEffect(function () {
        if (autoFocus) {
            inputRef.current.focus();
        }
    }, [autoFocus]);
    /* istanbul ignore next */
    useImperativeHandle(ref, function () { return inputRef.current; });
    var onClear = function () {
        var input = inputRef.current;
        setNativeValue(input, '');
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.focus();
    };
    var inputLength = typeof inputProps.value === 'string' && inputProps.value.length ? inputProps.value.length : 0;
    var maxLengthMessage = showMaxLengthMessage && props.maxLength && inputLength > 0
        ? "".concat(props.maxLength - ((_c = (inputProps.value || '')) === null || _c === void 0 ? void 0 : _c.length), " remaining")
        : "".concat(props.maxLength, " character limit");
    return (jsx(FormElement, __assign({ className: className, labelId: labelId, label: label, size: size, htmlFor: name, required: required && !hideAsterisk, hintMessageId: hintMessageId, hintMessage: hintMessage || (showMaxLengthMessage && maxLengthMessage), hasError: hasError, hasWarning: hasWarning, disabled: isDisabled, showOptionalLabel: showOptionalLabel }, { children: jsx(InputContextProvider, __assign({ value: { hasError: hasError, hasWarning: hasWarning, isDisabled: isDisabled, isReadOnly: isReadOnly } }, { children: jsxs("div", __assign({ className: clsx(styles.inputWrapper, wrapperClassName) }, { children: [leftElement, jsx("input", __assign({ id: id || name, type: type, "aria-describedby": "".concat(ariaDescribedby, " ").concat(hintMessageId), disabled: isDisabled, ref: inputRef, "aria-invalid": hasError, style: inputStyle, className: clsx(styles.inputField, (_b = {},
                            _b[styles.inputMedium] = size === 'm',
                            _b[styles.inputLarge] = size === 'l',
                            _b[styles.inputError] = hasError,
                            _b[styles.inputWarning] = hasWarning,
                            _b[styles.inputLeftIcon] = leftElementType === 'icon',
                            _b[styles.inputRightIcon] = rightElementType === 'icon',
                            _b[styles.inputRightIconWithClear] = rightElementType === 'icon' && isClearable,
                            _b[styles.inputLeftTrigger] = leftElementType === 'trigger',
                            _b[styles.inputRightTrigger] = rightElementType === 'trigger',
                            _b[styles.inputRightTriggerWithClear] = rightElementType === 'trigger' && isClearable,
                            _b), inputClassName), readOnly: isReadOnly }, props, inputProps)), isClearable && inputProps.value && (jsx(IconButton, __assign({ variant: "text", color: "secondary", size: size === 'l' ? 's' : 'xxs', onClick: onClear, "aria-label": "Clear value", "data-testid": "clear-value", className: styles.inputClearButton, disabled: isDisabled || isReadOnly }, { children: jsx(CloseIcon, { variant: size === 'l' ? 'large' : 'small' }) }))), rightElement] })) })) })));
});
// https://github.com/DefinitelyTyped/DefinitelyTyped/issues/34757#issuecomment-894053907
var Input = Object.assign(InputElement, { Skeleton: Skeleton, Icon: Icon, TriggerButton: TriggerButton });

export { Input, InputElement };
